/**
 * LIFECYCLE ENGAGEMENT — the build-out plan, rendered on /engagement.
 *
 * Evan (2026-08-14): "add an engagement page to admin dash for this with notes for how it should be
 * built out — i will build out in separate chat." This file IS those notes. It is the thing handed
 * to the next session, so it lives beside the numbers that justify it rather than in a doc nobody
 * opens: the page shows what is true today, this file says what to do about it.
 *
 * Keep it current the way `strategyMap.ts` and `handoffs.ts` are kept current — flip `status` as
 * phases land. Do not restate roadmap items from `docs/ROADMAP.md` here; this is the lifecycle
 * workstream only.
 */

export type PlanStatus = 'blocking' | 'next' | 'later' | 'done';

export type PlanPhase = {
  id: string;
  order: string;
  title: string;
  status: PlanStatus;
  /** Why this exists and why it is in this position in the order. */
  why: string;
  /** Concrete work items. */
  build: string[];
  /** The traps. Each one is something that will otherwise be discovered the expensive way. */
  warnings: string[];
  /** How we will know it worked — stated before it is built, on purpose. */
  acceptance: string;
};

export const PLAN_STATUS_META: Record<PlanStatus, { label: string; tone: string }> = {
  blocking: { label: 'Blocks everything', tone: 'admin-status-danger' },
  next: { label: 'Next', tone: 'admin-status-warning' },
  later: { label: 'Later', tone: 'admin-status-neutral' },
  done: { label: 'Done', tone: 'admin-status-success' },
};

export const ENGAGEMENT_PHASES: PlanPhase[] = [
  {
    id: 'instrumentation',
    order: 'Phase 0',
    title: 'Make return visits measurable at all',
    status: 'blocking',
    why:
      'Every phase below is judged by whether people came back, and right now the app cannot tell you that. `app_sessions` behaves like a first-open marker (the overwhelming majority of users have exactly one row, ever) and `app_events` stops firing after signup day for almost everyone — proven by the raters whose newest RATING is later than their newest event, on the Instrumentation panel above. Ratings are the only honest activity signal we have, and they under-count by definition: opening the app, browsing a city and leaving is invisible. Build a lifecycle program on top of that and you will be optimising a number that moves for reasons you cannot see.',
    build: [
      'Mobile repo: find the `app_sessions` writer and make it a real session. An AppState listener that closes the row on `background` and opens a new one on `active` after an idle threshold (~30 min) is the whole fix. Today a session is written once and never closed — hence the null `ended_at` / `duration_seconds` rows.',
      'Add `profiles.last_active_at timestamptz` + index, upserted from the client at most once an hour. One indexed column answers "who is slipping" in a single query; today that question requires scanning every rating row. It is also the column every cron job in Phase 2–4 will select on.',
      'Audit which screens fire `app_events`. `screen_view` covers 440 of 474 accounts on signup day and then goes quiet, so either the listener unmounts or the tracker is only wired into the entry route.',
      'DB: give the dashboard read access to the notification side of the workstream — see the warnings.',
    ],
    warnings: [
      '⚠️ `notifications` has no admin SELECT policy, so notification volume, read-rate and per-type performance are unmeasurable from here (the probe above proves it live — it returns only the signed-in admin\'s own rows). When adding one, use the existing `is_admin()` SECURITY DEFINER helper, NOT an inline `EXISTS (SELECT 1 FROM profiles …)`. `notifications` is in the same self-referencing-policy family that caused the 2026-06-29 replies recursion outage.',
      '⚠️ `user_push_tokens` has RLS enabled with ZERO policies. It does not error — it reads as empty. Push reachability ("how many users could we even reach") is therefore unknown, and any campaign sized without it is guesswork.',
      '⚠️ Do NOT fix `profiles.notification_preferences` / `expo_push_token` by adding them to the `authenticated` column grant. That grant is what every logged-in app user gets, not what admins get — it would hand every user\'s push token to every other user. The correct fix is a SECURITY DEFINER RPC (`admin_notification_health()`) returning AGGREGATES only.',
      '⚠️ Do not promote `app_events` or `app_sessions` to a headline metric on this page until the Instrumentation panel shows the gap closed. The comment block at the top of `src/lib/engagementMetrics.ts` explains the exact failure and must be updated in the same commit if this changes.',
    ],
    acceptance:
      'The Instrumentation panel on this page shows distinct active days per user tracking rating-days instead of collapsing to 1, and `ratersRatingNewerThanEvent` falls to near zero.',
  },
  {
    id: 'streaks',
    order: 'Phase 1',
    title: 'Streaks — in WEEKS, never days',
    status: 'next',
    why:
      'A daily streak is the wrong unit for this product and would actively hurt it: nobody goes out seven nights a week, so a daily counter breaks every Monday and trains users to ignore it. The data says weeks work — the longest-run distribution above is measured in consecutive rating weeks and it is not degenerate; a real minority have strung several together with no streak feature existing at all. That is the behaviour to name and reward, not invent.',
    build: [
      'Server-side `user_streaks` (user_id, current_weeks, longest_weeks, last_week_start, grace_used_week), recomputed from `logs` in the same path that already recomputes venue scores.',
      'Monday-start weeks, matching Postgres `date_trunc(\'week\', …)` and this page. Any other boundary and the app and the dashboard will quietly disagree.',
      'One grace week: a run survives a single missed week. This page already models it (`currentWeekRun` counts a run alive if its last week is this week OR last week) — keep the two definitions identical.',
      'Surface: a "your week" strip on the profile plus the streak count in the post-rating reveal sequence, which is already the app\'s celebration moment (`components/PostRatingRevealSequence.tsx`).',
    ],
    warnings: [
      '⚠️ Compute the streak on the SERVER from `logs`, never on the client. The retire-and-create edit flow (`app/log-venue.tsx`) writes a new row and sets `retired_at` on the old one — a client-side counter that misses the `.is(\'retired_at\', null)` filter will break someone\'s streak when they merely EDIT a rating.',
      '⚠️ A streak is a loss-aversion mechanic. Shipping it without the Phase 2 nudge means users lose streaks silently and the feature becomes a source of resentment rather than return visits. Ship 1 and 2 together or ship neither.',
    ],
    acceptance:
      'The current-run distribution on this page shifts right — more users on a 2+ week run — within four weeks of launch, against the pre-launch baseline captured here.',
  },
  {
    id: 'slipping-nudge',
    order: 'Phase 2',
    title: 'The slipping nudge — the highest-value audience on this page',
    status: 'next',
    why:
      'Look at the segment table. "Slipping" (8–28 days since last rating) is the only group that is simultaneously large, still in the habit, and still reachable. Active users need nothing; Dormant and Lost need a reason to reinstall a habit. Slipping users need one reason to open the app this weekend. Every hour spent on win-back before this is spent on the harder problem first.',
    build: [
      'A pg_cron job that selects the Slipping segment and sends via the existing `send-push-notification` Edge function.',
      'Content is friend activity, not absence: "3 people you follow rated somewhere this week" beats "we miss you" by a wide margin because it is information, not guilt. The follow graph already supports this (`mutual_follow_user_ids`).',
      'Timing: Thursday evening. This audience decides its weekend on Thursday/Friday, and the weekly-active chart on this page shows the rhythm.',
      'A permanent holdout (~20%) that receives nothing, recorded per user, so the segment-migration measurement below is real.',
    ],
    warnings: [
      '⚠️ There is a suspected DOUBLE-SEND on the existing push path (see the push-notifications notes: `send_push_notification` + the notifications webhook both live only in prod, not in migrations). Resolve that before adding campaign volume, or the first lifecycle send becomes the first lifecycle complaint.',
      '⚠️ There is no per-user timezone in `profiles`. Deriving one from `city` is fine as a v1 but must be explicit — a "Thursday 6pm" send that lands at 3pm on the west coast is a worse push than none.',
      '⚠️ Respect `notification_preferences` at the QUERY level, not in the client. Phase 0 makes that readable to a server function.',
      '⚠️ Frequency cap before content variety: at most one lifecycle push per user per week across ALL campaigns in this plan, enforced centrally, not per-campaign.',
    ],
    acceptance:
      'Slipping→Active migration rate over 28 days is measurably higher in the sent group than the holdout, read off this page\'s segment table with the holdout split added.',
  },
  {
    id: 'winback',
    order: 'Phase 3',
    title: 'Win-back for dormant users',
    status: 'later',
    why:
      'The win-back panel above is the case for and the sizing of this phase in one place: it counts users who went quiet for three weeks or more and came back with no prompting at all, and the median length of that gap is the window a campaign should target. Organic returns prove the behaviour exists; the campaign is only trying to make it more likely and sooner.',
    build: [
      'One send, capped at once per 30 days per user, aimed at the Dormant segment.',
      'Content is what changed in THEIR city while they were gone — new venues rated, movement in the rankings. The city page already computes all of it.',
      'Use the median organic return gap on this page as the send offset rather than a round number pulled from a blog post.',
    ],
    warnings: [
      '⚠️ Do not send to "Lost" (>90 days) until win-back is proven on Dormant. A campaign that fails on a warm audience will not succeed on a cold one, and every send to a dead account is a chance at an uninstall.',
      '⚠️ Hold out permanently, not for one cycle. Resurrection happens organically at a measurable rate — without a holdout you will attribute the organic rate to the campaign and conclude it works.',
    ],
    acceptance:
      'Resurrection rate in the sent group beats the organic baseline recorded on this page today, at n large enough to be worth acting on.',
  },
  {
    id: 'digest',
    order: 'Phase 4',
    title: 'Weekly digest',
    status: 'later',
    why:
      'Every phase above requires the user to have done something. A Sunday digest — your week, your friends\' week, how your city moved — is the only lifecycle surface that works on someone who did nothing, which is most of the account base.',
    build: [
      'Sunday send summarising the Monday-start week that just closed, so it lines up with the streak week and this page.',
      'Reuse the existing brief infrastructure (`send-morning-brief` / `send-evening-brief` Edge functions) rather than building a fourth sender.',
      'Skip the send entirely for users with nothing to report. An empty digest is worse than silence.',
    ],
    warnings: [
      '⚠️ A recurring send needs an unsubscribe that genuinely works, which depends on Phase 0\'s `notification_preferences` access. Shipping a digest that ignores a mute is how an app gets notifications disabled at the OS level — permanently, for every other campaign here.',
    ],
    acceptance:
      'Digest-day open rate and the Monday rating count both lift, without a rise in notification-permission revocations.',
  },
  {
    id: 'triggered',
    order: 'Phase 5',
    title: 'Activity-triggered prompts',
    status: 'later',
    why:
      'The highest-intent moment to ask for a rating is the night of the visit, not three days later. This is the most effective idea in the plan and the most dangerous to ship, which is why it is last.',
    build: [
      'Prompt to rate when a user has been near a venue in the map cache during opening hours and has not logged it.',
      'Start with a foreground-only version: prompt on next app open if the last known location matched a venue. No background permission, no review risk, most of the value.',
    ],
    warnings: [
      '⚠️ Background location is an App Store review risk and a battery complaint generator, and the listing is already held pending the Apple account migration. Do not add a background-location entitlement before launch under any circumstances.',
      '⚠️ Getting this wrong is uniquely costly: a wrong-venue prompt reads as surveillance rather than a bug.',
    ],
    acceptance:
      'Ratings per active user rises without a fall in rating quality (comparisons completed per rating started).',
  },
  {
    id: 'measurement',
    order: 'Phase 6',
    title: 'Make every send falsifiable',
    status: 'later',
    why:
      'Without this, phases 2–5 cannot be evaluated, only believed. Every campaign in this plan asserts a lift; none of them can prove one against a background rate that this page shows is already non-zero.',
    build: [
      '`lifecycle_sends` (user_id, campaign, variant, sent_at, opened_at, converted_at) written by every sender.',
      'A holdout column on the user, assigned once and never reassigned, shared across all campaigns.',
      'A per-campaign section on THIS page: sent, opened, converted, and the same numbers for the holdout beside them.',
    ],
    warnings: [
      '⚠️ Assign the holdout once, globally. A per-campaign holdout means a user can be in the control for one campaign and the treatment for another, and every result becomes uninterpretable.',
    ],
    acceptance:
      'Every campaign above can be read off one table with its control beside it.',
  },
];

/** Definitions this page asserts. Written down so the app and the dashboard cannot drift apart. */
export const ENGAGEMENT_DEFINITIONS: { term: string; meaning: string }[] = [
  {
    term: 'Active day',
    meaning:
      'A calendar day (midnight America/Chicago, the same boundary the Overview charts use) on which a user created a non-retired, non-deleted rating.',
  },
  {
    term: 'Week',
    meaning: 'Monday-start, matching Postgres date_trunc(\'week\', …). Streaks must use the same boundary.',
  },
  {
    term: 'Active / Slipping / Dormant / Lost',
    meaning: 'Days since last rating: ≤7 / 8–28 / 29–90 / >90. Accounts that have never rated are counted separately.',
  },
  {
    term: 'Lapsed',
    meaning:
      'A gap of 21+ days between consecutive rating days. Chosen over 14 (which over-counts people who simply skipped a fortnight) and over 30 (too late to intervene).',
  },
  {
    term: 'Resurrection rate',
    meaning:
      'Users who returned after a 21+ day gap, over everyone who has ever been quiet that long — including those still quiet. Dividing by "currently lapsed" alone would flatter it, because returners are by definition no longer in that group.',
  },
  {
    term: 'Cohort retention',
    meaning:
      'Rated at least once in week N after signup. Cells are blank, not zero, where the window has not fully elapsed.',
  },
];

/**
 * The paste-ready handoff, matching the `/development` convention so Evan can hand this to a
 * session in one click. Keep it in sync with `ENGAGEMENT_PHASES` above — it is the same plan
 * compressed, not a second plan.
 */
export const ENGAGEMENT_HANDOFF_PROMPT = `TASK — LIFECYCLE ENGAGEMENT: streaks, re-engagement notifications, weekly digest, win-back (mobile repo + Supabase + Edge)

Read first: _master/CLAUDE.md, _master/DISPATCH_HANDOFF.md, _master/OUTSTANDING.md, _master/WORKSTREAMS.md.
The measurement layer already exists: shado-admin /engagement (src/pages/EngagementScreen.tsx + src/lib/engagementMetrics.ts + src/data/engagementPlan.ts). Open it before writing code — it holds the live baseline every phase below is judged against, and its header comment explains why ratings, not sessions, are the activity signal.

ORDER IS NOT NEGOTIABLE. Phase 0 blocks everything.

PHASE 0 — MAKE RETURN VISITS MEASURABLE
- app_sessions is a first-open marker: most users have exactly one row ever, ended_at/duration_seconds null. Fix the writer with an AppState listener (close on background, new session on active after ~30 min idle).
- app_events stops after signup day. Proof: a large majority of raters have a rating NEWER than their newest app_event. Audit which screens actually fire it.
- Add profiles.last_active_at timestamptz + index, upserted at most hourly. Every lifecycle cron selects on this.
- DB access, all Tier-2, one at a time with Evan's approval:
  * notifications: no admin SELECT policy exists. Add one using the existing is_admin() SECURITY DEFINER helper. DO NOT write an inline EXISTS(SELECT 1 FROM profiles ...) — notifications is in the same self-referencing family that caused the 2026-06-29 replies recursion outage.
  * user_push_tokens: RLS enabled with ZERO policies, so it reads as empty rather than erroring. Add own-row + admin SELECT.
  * notification_preferences / expo_push_token: DO NOT add these to the authenticated column grant on profiles — that grant is what every app user gets and would expose every user's push token. Add a SECURITY DEFINER RPC admin_notification_health() returning aggregates only.

PHASE 1 — STREAKS IN WEEKS, NOT DAYS
- Weekly, Monday-start (matches Postgres date_trunc('week') and the admin page). Daily streaks are wrong for nightlife and would break every Monday.
- Server-side user_streaks table recomputed from logs. NEVER compute on the client: the retire-and-create edit flow sets retired_at and would break a streak on an edit.
- One grace week (a run survives one missed week) — identical to the admin page's definition.
- Surface on the profile and in PostRatingRevealSequence.
- Ship WITH Phase 2. A streak mechanic without a nudge just makes users lose streaks silently.

PHASE 2 — SLIPPING NUDGE (do this before win-back)
- Audience: 8–28 days since last rating. Largest reachable group still in the habit.
- Thursday evening send via the existing send-push-notification Edge function.
- Content is friend activity ("3 people you follow rated this week"), not "we miss you".
- BLOCKER: resolve the suspected double-send on the existing push path first.
- No per-user timezone exists; derive from profiles.city as v1 and say so.
- Global frequency cap: max one lifecycle push per user per week across ALL campaigns.
- 20% permanent holdout, assigned once globally.

PHASE 3 — WIN-BACK (dormant, 29–90 days)
- Use the median organic return gap shown on the admin page as the send offset.
- Content: what changed in their city while they were gone.
- Do not send to Lost (>90d) until this is proven on Dormant. Hold out permanently — organic resurrection is non-zero and will otherwise be credited to the campaign.

PHASE 4 — WEEKLY DIGEST
- Sunday, covering the Monday-start week that just closed. Reuse send-morning-brief / send-evening-brief infrastructure.
- Skip users with nothing to report. Requires a working unsubscribe (depends on Phase 0).

PHASE 5 — TRIGGERED PROMPTS
- Foreground-only first: prompt on next open if last known location matched a venue. NO background location entitlement before launch — App Store review risk while the listing is held pending the Apple migration.

PHASE 6 — FALSIFIABILITY
- lifecycle_sends (user_id, campaign, variant, sent_at, opened_at, converted_at) written by every sender, plus a global holdout column assigned once. Add a per-campaign section to the admin /engagement page reading it.

HOUSE RULES
- Guardrail tiers: every migration, policy, cron and Edge deploy is Tier 2+ — state the plan and wait.
- Verify prod schema with pg_get_functiondef / information_schema before building on any column; repo migrations lag production.
- New UI: rs() / hs() scaling, Dynamic Type immunity, never call hs/rs/tf inside a Reanimated worklet.
- Update _master/CLAUDE_WORK_LOG.md with root causes as you go; surface launch-blockers to _master/OUTSTANDING.md.`;
